/**
 * Matrix client listener registration and auth/sync error handlers.
 */

import { addBreadcrumb } from 'shared/sentry';
import { isTokenExpiredError } from './matrixAuth';
import { isAbortError, isSameMatrixClient, safeListener } from './matrixUtils';
import { markMatrixSyncSuccess } from './matrixTabResume';

/**
 * Track client listeners so we can remove them if init fails or the client is replaced.
 * @param {import('./matrixUtils').MatrixService} service
 * @param {import('matrix-js-sdk').MatrixClient} client
 * @param {string} event
 * @param {Function} handler
 */
export function registerClientListener(service, client, event, handler) {
  if (!client?.on) return;
  client.on(event, handler);
  service._clientListenerDisposers.push(() => {
    try {
      client.removeListener?.(event, handler);
    } catch (_) {
      // ignore
    }
  });
}

/**
 * Setup event handlers for Matrix client authentication errors.
 * @param {import('./matrixUtils').MatrixService} service
 */
export function setupAuthErrorHandlers(service) {
  if (!service.matrixClient) return;

  const clientRef = service.matrixClient;

  registerClientListener(
    service,
    service.matrixClient,
    'Session.logged_out',
    safeListener(async (err) => {
      if (!isSameMatrixClient(service, clientRef)) return;
      if (!err) return;
      if (isAbortError(err)) return;
      if (isTokenExpiredError(err)) {
        // Expected recovery path — breadcrumb only (handleTokenExpiry reports real failures).
        addBreadcrumb({
          category: 'matrix.auth',
          message: 'session logged out (token expiry); recovering',
          level: 'info',
        });
        await service.handleTokenExpiry('Session.logged_out', err);
      }
    }, 'Session.logged_out'),
  );

  registerClientListener(
    service,
    service.matrixClient,
    'sync',
    safeListener(async (state, prevState, data) => {
      if (!isSameMatrixClient(service, clientRef)) return;

      if (data?.error) {
        if (isAbortError(data.error)) {
          return;
        }
        if (isTokenExpiredError(data.error)) {
          addBreadcrumb({
            category: 'matrix.auth',
            message: 'sync token expiry; recovering',
            level: 'info',
            data: { syncState: state },
          });
          await service.handleTokenExpiry(`sync:${state}`, data.error);
          return;
        }
      }

      if (state === 'PREPARED' || state === 'SYNCING') {
        markMatrixSyncSuccess();
      }

      if ((state === 'ERROR' || state === 'STOPPED') && !service.syncRestartPromise) {
        await service.requestSyncRecovery({ reason: `sync:${state}` });
      }
    }, 'sync'),
  );

  registerClientListener(
    service,
    service.matrixClient,
    'httpError',
    safeListener(async (error) => {
      if (!isSameMatrixClient(service, clientRef)) return;
      if (!error) return;
      if (isAbortError(error)) return;
      if (isTokenExpiredError(error)) {
        addBreadcrumb({
          category: 'matrix.auth',
          message: 'http token expiry; recovering',
          level: 'info',
        });
        await service.handleTokenExpiry('httpError', error);
      }
    }, 'httpError'),
  );
}
